import { IonInput } from '@ionic/angular';
import { Subscription } from 'rxjs';
import * as leaflet from 'leaflet';
import 'leaflet.markercluster';

import Address from 'src/smoothr-web-app-core/models/Address';
import { getLatLng, sleep } from 'src/smoothr-web-app-core/utils/utils';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import { NominatimPlace } from 'src/smoothr-web-app-core/models/NominatimPlace';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import Order from 'src/smoothr-web-app-core/models/Order';

export const selectedMark = '/assets/icon/maps_marker_selected.svg';
export const notSelectedMark = '/assets/icon/maps_marker.svg';

export class MapsUtils {
	static initAutocomplete(
		ionInput: IonInput,
		callback: (predictions: Address[]) => void,
		loadingCallback: (loading: boolean) => void
	): Subscription {
		let prevRequest: Promise<Address[]> = null;
		return ionInput.ionChange.subscribe(async event => {
			if (prevRequest) {
				await prevRequest;
				await sleep(100);
			}
			loadingCallback(true);
			const value = event.detail.value;
			if (value.replace(' ', '').length === 0) {
				callback([]);
				loadingCallback(false);
				return;
			}
			prevRequest = new Promise<Address[]>(async (resolve, reject) => {
				try {
					resolve(await this.getNominatimPrediction(value));
				} catch (e) {
					reject(e);
				}
			});
			callback(await prevRequest);
			loadingCallback(false);
			prevRequest = null;
		});
	}

	static async getNominatimPrediction(input: string): Promise<Address[]> {
		const data = (await Api.mapSearch(input, 5)).data;
		console.log(
			data.map(nplace => ({
				title: nplace.display_name,
				importance: nplace.importance,
				category: nplace.category,
			}))
		);
		const addresses = data
			.map(nplace => this.placeToAddress(nplace))
			.filter(address => !ValidationUtils.validateAddress(address, false, false));
		const titles = addresses.map(address => this.addressToString(address));
		return addresses.filter((address, addressIndex) => {
			return titles.indexOf(titles[addressIndex]) === addressIndex;
		});
	}

	static checkAddress(address: Address): string {
		const addressError = ValidationUtils.validateAddress(address, false, false);
		if (addressError) {
			console.error('Maps error: ' + addressError);
			throw addressError;
		}
		return this.addressToString(address);
	}

	static async executeSearch(ionInput: IonInput): Promise<Address> {
		return new Promise<Address>(async (resolve, reject) => {
			const input = await ionInput.getInputElement();
			const inputValue = input.value;
			if (!inputValue || inputValue.length <= 0) {
				reject();
				return;
			}
			try {
				const result = MapsUtils.getPlace(inputValue);
				if (result) {
					resolve(result);
				} else {
					reject('No results');
				}
				return;
			} catch (e) {
				reject(e);
			}
		});
	}

	static async getPlace(pred: string): Promise<Address> {
		return new Promise<Address>(async (resolve, reject) => {
			try {
				const predictions = await this.getNominatimPrediction(pred);
				if (predictions.length < 1) {
					resolve(null);
					return;
				}
				resolve(predictions[0]);
			} catch (e) {
				reject(e);
			}
		});
	}

	static placeToAddress(place: NominatimPlace): Address {
		if (!place) {
			return null;
		}
		const address = new Address();
		address.number = place.address.house_number || place.address.house_name || null;
		address.street = place.address.road || place.address.construction || null;
		address.city = place.address.city || place.address.town || place.address.village || place.address.municipality || null;
		address.state =
			place.address.state ||
			place.address.region ||
			place.address.state_district ||
			place.address.county ||
			place.address.city ||
			null;
		address.country = place.address.country || place.address.country_code || null;
		address.postalCode = place.address.postcode || null;
		address.lat = +place.lat || null;
		address.lng = +place.lon || null;
		address.placeId = place.place_id;
		address.displayName = place.display_name;
		return address;
	}

	static addressToString(address: Address): string {
		if (!address) {
			return '';
		}
		if (address.displayName?.length > 0) {
			return address.displayName;
		}
		let title = '';
		if (address.street) {
			title += address.street;
		}
		if (address.number) {
			title += ' ' + address.number;
		}
		if (title !== '' && (address.postalCode || address.city)) {
			title += ',';
		}
		if (address.postalCode) {
			if (title !== '') {
				title += ' ';
			}
			title += address.postalCode;
		}
		if (address.city) {
			if (title !== '') {
				title += ' ';
			}
			title += address.city;
		}

		return title;
	}

	static async getUserGeocode(): Promise<Address> {
		const geocoderPromise = new Promise<Address>((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(
				async position => {
					try {
						const result = (await Api.geocode(position.coords.latitude, position.coords.longitude)).data;
						console.log(result);
						if (result) {
							resolve(MapsUtils.placeToAddress(result));
						} else {
							reject('No result');
						}
					} catch (e) {
						reject(e);
					}
				},
				error => reject(error)
			);
		});
		const timeoutPromise = new Promise<Address>((resolve, reject) => {
			setTimeout(() => reject('Timeout'), 10000);
		});
		return Promise.race([geocoderPromise, timeoutPromise]);
	}

	static getClusterIcon(count: number) {
		const svg = `
		<svg width="53" height="69" viewBox="0 0 53 69" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M24.1641 0.0399399C23.9483 0.0669098 23.2741 0.161301 22.6808 0.24221C14.9 1.25358 7.78 6.00025 3.64015 12.9045C-1.44364 21.4134 -1.18743 32.498 4.31439 40.7103C5.3797 42.288 24.1236 67.5586 24.69 68.1789C24.9193 68.4217 25.2968 68.7183 25.5396 68.8127C26.6049 69.2712 27.3196 68.9206 28.4523 67.3968C38.0131 54.5592 47.9109 41.1688 48.4773 40.2788C51.2552 35.9097 52.725 30.4213 52.5093 25.3106C52.1991 18.4872 49.5696 12.4865 44.7959 7.71283C40.7909 3.69434 36.0173 1.25358 30.2997 0.29615C29.0726 0.0938797 25.0136 -0.0814209 24.1641 0.0399399ZM29.7738 12.5404C34.7632 13.8484 38.6199 17.6916 39.9684 22.6945C40.292 23.8812 40.3325 24.2587 40.3325 26.2545C40.3325 28.2503 40.292 28.6278 39.9684 29.8145C38.6199 34.8174 34.8037 38.6201 29.7738 39.9821C28.6276 40.2922 28.223 40.3327 26.2543 40.3327C24.2585 40.3327 23.8809 40.2922 22.6943 39.9686C17.6914 38.6201 13.8886 34.8039 12.5267 29.7741C12.2165 28.6278 12.1761 28.2233 12.1761 26.2545C12.1761 24.2857 12.2165 23.8812 12.5267 22.735C13.9696 17.395 18.042 13.5383 23.4494 12.3651C25.2294 11.9875 27.9399 12.0549 29.7738 12.5404Z" fill="#DB052C"/>
		<circle cx="26.2" cy="26.1" r="13.3" fill="#D9D9D9"/>
		<text x="26.2" y="26.1" text-anchor="middle" dominant-baseline="middle" fill="#000000" font-size="16" font-weight="bold" class="marker-number" style="font-family: 'Roboto', sans-serif;">${count}</text>
		</svg>
		`;
		const blob = new Blob([svg], { type: 'image/svg+xml' });
		const url = URL.createObjectURL(blob);

		return leaflet.icon({
			iconUrl: url,
			iconSize: [25 * 2, 41 * 2],
			iconAnchor: [12, 41],
			popupAnchor: [1, -34],
		});
	}

	static addVenuesToMap(
		clusterGroup: leaflet.MarkerClusterGroup,
		selectedVenue: Venue,
		venues: Venue[],
		map: leaflet.Map,
		onClick: (venue: Venue) => void
	): leaflet.MarkerClusterGroup {
		clusterGroup =
			clusterGroup ||
			leaflet.markerClusterGroup({
				chunkedLoading: true,
				maxClusterRadius: 30,
				zoomToBoundsOnClick: true,
				animateAddingMarkers: true,
				animate: true,
				iconCreateFunction: function (cluster: any) {
					return MapsUtils.getClusterIcon(cluster.getAllChildMarkers().length);
				},
			});

		// Clear existing markers
		clusterGroup.clearLayers();
		if (!map.hasLayer(clusterGroup)) {
			clusterGroup.addTo(map);
		}

		const newVenues = venues.filter(
			ven =>
				clusterGroup
					.getLayers()
					.flatMap((cluster: leaflet.MarkerCluster) => cluster.getAllChildMarkers())
					.find(
						mark =>
							ven.location?.coordinates[1] === mark.getLatLng().lat && ven.location?.coordinates[0] === mark.getLatLng().lng
					) === undefined
		);
		const marksToRemove = clusterGroup.getLayers().flatMap((cluster: leaflet.MarkerCluster) =>
			cluster
				.getAllChildMarkers()
				.map((mark, index) =>
					venues.find(
						ven =>
							ven.location?.coordinates[1] === mark.getLatLng().lat && ven.location?.coordinates[0] === mark.getLatLng().lng
					) === undefined
						? index
						: -1
				)
				.filter(i => i >= 0)
		);

		for (const markToRemove of marksToRemove.reverse()) {
			clusterGroup.removeLayer(clusterGroup.getLayers()[markToRemove]);
		}

		const bounds = leaflet.latLngBounds([]);
		const markers: leaflet.Marker[] = [];

		for (const venue of venues) {
			const latLng = getLatLng(venue);
			if (!latLng) {
				continue;
			}

			const position = leaflet.latLng(latLng.latitude, latLng.longitude);
			if (latLng.latitude !== 0 && latLng.longitude !== 0) {
				bounds.extend(position);
			}

			if (newVenues.find(ven => ven._id === venue._id)) {
				const marker = leaflet.marker(position, {
					icon: leaflet.icon({
						iconUrl: notSelectedMark,
						iconSize: [25 * 1.5, 41 * 1.5],
						iconAnchor: [12, 41],
						popupAnchor: [1, -34],
					}),
				});

				marker.on('click', () => {
					onClick(venue);
				});

				markers.push(marker);
			}
		}

		// Add all markers to the cluster group
		clusterGroup.addLayers(markers);

		// If there's a selected venue, highlight it and center the map
		if (selectedVenue && selectedVenue.location) {
			const selectedMarker = markers.find(marker => {
				const pos = marker.getLatLng();
				return selectedVenue.location.coordinates[1] === pos.lat && selectedVenue.location.coordinates[0] === pos.lng;
			});

			if (selectedMarker) {
				selectedMarker.setIcon(
					leaflet.icon({
						iconUrl: selectedMark,
						iconSize: [25 * 1.8, 41 * 1.8],
						iconAnchor: [12, 41],
						popupAnchor: [1, -34],
					})
				);

				const pos = selectedMarker.getLatLng();
				const selectedBounds = leaflet.latLngBounds(
					leaflet.latLng(pos.lat - 0.01, pos.lng - 0.01),
					leaflet.latLng(pos.lat + 0.01, pos.lng + 0.01)
				);
				map.fitBounds(selectedBounds);
				map.panTo(pos);
			}
		} else if (bounds.getNorthEast() && bounds.getSouthWest()) {
			map.fitBounds(bounds, {
				padding: [50, 50],
			});
		} else {
			map.setView(leaflet.latLng(51.1657, 10.4515), 6);
		}

		return clusterGroup;
	}

	static scrollToMap(clusterGroup: leaflet.MarkerClusterGroup, map: leaflet.Map, selectedVenue: Venue): leaflet.MarkerClusterGroup {
		if (selectedVenue && selectedVenue.location) {
			const markers = clusterGroup.getLayers() as leaflet.Marker[];
			const selectedMarker = markers.find(marker => {
				const pos = marker.getLatLng();
				return selectedVenue.location.coordinates[1] === pos.lat && selectedVenue.location.coordinates[0] === pos.lng;
			});

			if (selectedMarker) {
				selectedMarker.setIcon(
					leaflet.icon({
						iconUrl: selectedMark,
						iconSize: [25, 41],
						iconAnchor: [12, 41],
						popupAnchor: [1, -34],
					})
				);

				const pos = selectedMarker.getLatLng();
				const selectedBounds = leaflet.latLngBounds(
					leaflet.latLng(pos.lat - 0.01, pos.lng - 0.01),
					leaflet.latLng(pos.lat + 0.01, pos.lng + 0.01)
				);
				map.fitBounds(selectedBounds);
				map.panTo(pos);
			}
		}
		return clusterGroup;
	}
}

export const calculateGeoDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
	const r = 6371; // Radius of the earth in km
	const dLat = deg2rad(lat2 - lat1);
	const dLon = deg2rad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return r * c; // Distance in km
};

export const deg2rad = (deg: number) => {
	return deg * (Math.PI / 180);
};

export const venueToAddress = (venue: Venue) => {
	const address = new Address();
	address.city = venue.city.de;
	address.lat = venue.location.coordinates[1];
	address.lng = venue.location.coordinates[0];
	address.street = venue.street;
	address.number = venue.number;
	address.postalCode = venue.postalCode;
	return address;
};

export const orderToAddress = (order: Order) => {
	const address = new Address();
	address.city = order.preorder.city;
	address.lat = order.preorder.lat;
	address.lng = order.preorder.lng;
	address.street = order.preorder.street;
	address.number = order.preorder.number;
	address.postalCode = order.preorder.postalCode;
	return address;
};
